/**
 * Registration form modal for OfflineMeeting.
 * Mirrors kinasticCoach's OfflineMeetingFormModal:
 * - Consent checkbox (for new registrations)
 * - Registration fields (text inputs)
 * - Save / Unregister actions
 */
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { OfflineMeetingEntry } from '../../../Model/Explore/OfflineMeetingEntry';
import { FormField } from '../../../Model/Explore/FormField';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Theme } from '../../../Theme/Theme';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { useLocalized } from '../../../Hooks/useLocalized';
import dayjs from 'dayjs';

const Overlay = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  justify-content: center;
  align-items: center;
  padding: 16px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${Theme.Colors.lightestGrey};
`;

const ModalTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  color: ${Theme.Colors.darkGrey};
  &:hover {
    color: ${Theme.Colors.textColor};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConsentCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
`;

const Checkbox = styled.input`
  margin-top: 2px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  accent-color: ${Theme.Colors.primary};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const FieldInput = styled.input`
  padding: 12px 14px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 10px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.15s;
  &:focus {
    border-color: ${Theme.Colors.primary};
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  background: ${Theme.Colors.primary};
  color: white;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UnregisterButton = styled.button`
  width: 100%;
  padding: 10px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: #d32f2f;
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 0.85rem;
  text-align: center;
`;

export type OfflineMeetingRegistrationModalProps = {
  visible: boolean;
  onClose: () => void;
  entry: OfflineMeetingEntry;
  existingEvent: Record<string, any>;
  isSubscribed: boolean;
  onSave: () => void;
  onUnregister: () => void;
};

export const OfflineMeetingRegistrationModal: React.FC<OfflineMeetingRegistrationModalProps> =
  observer(({ visible, onClose, entry, existingEvent, isSubscribed, onSave, onUnregister }) => {
    const { t } = useTrans();

    const [consentChecked, setConsentChecked] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

    // Initialize field values from existing event or entry
    useEffect(() => {
      if (!visible) return;
      const values: Record<string, string> = {};
      const fields = existingEvent?.registrationFields ?? entry.registrationFields;
      fields.forEach((f: any) => {
        values[f.identifier] = f.value ?? '';
      });
      setFieldValues(values);
      setConsentChecked(isSubscribed);
      setError('');
    }, [visible, existingEvent, entry.registrationFields, isSubscribed]);

    const handleFieldChange = useCallback((identifier: string, value: string) => {
      setFieldValues((prev) => ({ ...prev, [identifier]: value }));
    }, []);

    const canUnsubscribe = isSubscribed && (!entry.registerUntilDate || dayjs(entry.registerUntilDate).isAfter(dayjs()));

    const handleSave = useCallback(async () => {
      // Validate fields
      const allValid = entry.registrationFields.every((f) => {
        if (f.valueType === 'string' || f.valueType === 'email' || f.valueType === 'numeric') {
          const val = (fieldValues[f.identifier] ?? '').trim();
          const minSize = f.minSize ?? 1;
          return val.length >= minSize;
        }
        return true;
      });

      if (!allValid) {
        setError(
          t('exploreModal.offlineMeeting.fillAllFields', {
            defaultValue: 'Please fill in all required fields',
          }),
        );
        return;
      }

      setSaving(true);
      setError('');

      try {
        const payload = {
          ...existingEvent,
          registrationFields: entry.registrationFields.map((f) => ({
            identifier: f.identifier,
            label: f.label,
            value: fieldValues[f.identifier] ?? '',
            minSize: f.minSize,
            maxSize: f.maxSize,
            valueType: f.valueType,
          })),
        };
        await HttpBackend.post('/coach/schedule/event/subscription', payload);
        onSave();
      } catch (err) {
        setError(
          t('exploreModal.offlineMeeting.saveFailed', {
            defaultValue: 'Registration failed. Please try again.',
          }),
        );
      } finally {
        setSaving(false);
      }
    }, [entry, existingEvent, fieldValues, onSave, t]);

    const handleUnregister = useCallback(async () => {
      if (!existingEvent?.id) return;
      setSaving(true);
      setError('');
      try {
        await HttpBackend.delete(`/coach/schedule/event/subscription/${existingEvent.id}`);
        onUnregister();
      } catch (err) {
        setError(
          t('exploreModal.offlineMeeting.unregisterFailed', {
            defaultValue: 'Unregistration failed. Please try again.',
          }),
        );
      } finally {
        setSaving(false);
      }
    }, [existingEvent, onUnregister, t]);

    return (
      <Overlay visible={visible} onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {t('explore.offlineMeetingFormModal.title', { defaultValue: 'Register' })}
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <i className="bi bi-x-lg" />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {!isSubscribed && (
              <ConsentCard>
                <Checkbox
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                />
                <span>
                  {t('exploreModal.offlineMeeting.registrationField.consent', {
                    defaultValue: 'I agree to share the following information with a 3rd party',
                  })}
                </span>
              </ConsentCard>
            )}

            {entry.registrationFields.map((field) => (
              <RegistrationFieldInput
                key={field.identifier}
                field={field}
                value={fieldValues[field.identifier] ?? ''}
                onChange={(val) => handleFieldChange(field.identifier, val)}
              />
            ))}
          </ModalBody>

          <ModalFooter>
            {error && <ErrorText>{error}</ErrorText>}
            <SaveButton
              disabled={(!isSubscribed && !consentChecked) || saving}
              onClick={handleSave}
            >
              {saving
                ? t('common.saving', { defaultValue: 'Saving...' })
                : t('exploreModal.offlineMeeting.save', { defaultValue: 'Save' })}
            </SaveButton>
            {canUnsubscribe && (
              <UnregisterButton onClick={handleUnregister} disabled={saving}>
                {t('exploreModal.offlineMeeting.unregister', { defaultValue: 'Unregister' })}
              </UnregisterButton>
            )}
          </ModalFooter>
        </ModalContainer>
      </Overlay>
    );
  });

type RegistrationFieldInputProps = {
  field: FormField;
  value: string;
  onChange: (value: string) => void;
};

const RegistrationFieldInput: React.FC<RegistrationFieldInputProps> = ({ field, value, onChange }) => {
  const label = useLocalized(field.label, field.identifier);

  const inputType = (() => {
    switch (field.valueType) {
      case 'email':
        return 'email';
      case 'numeric':
        return 'number';
      default:
        return 'text';
    }
  })();

  return (
    <FieldGroup>
      <FieldLabel>{label}</FieldLabel>
      <FieldInput
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
      />
    </FieldGroup>
  );
};
